import { useEffect, useMemo, useState, useCallback } from "react";
import { Package, Plus, Minus, Trash2, Check, AlertTriangle, HandCoins, Upload, FileCheck, RotateCcw, ArrowUp, ArrowDown, ArrowUpDown, Printer } from "lucide-react";
import api, { loanJustificatifUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateLocale } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toast } from "sonner";

export default function InventairePage() {
  const { user, group, t, lang } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState({ items: [], members: [], states: {} });
  const [loans, setLoans] = useState([]);
  const [newItem, setNewItem] = useState("");
  // Zoom de la matrice (niveau partagé avec le tableau des présences), persisté sur l'appareil
  const [zoom, setZoom] = useState(() => {
    const z = parseFloat(localStorage.getItem("presence_zoom"));
    return isNaN(z) ? 0.85 : Math.min(1, Math.max(0.6, z));
  });
  useEffect(() => { localStorage.setItem("presence_zoom", String(zoom)); }, [zoom]);
  const zoomBy = (d) => setZoom((z) => Math.min(1, Math.max(0.6, Math.round((z + d) * 100) / 100)));
  const [newCat, setNewCat] = useState("obligatoire");
  const [newFamily, setNewFamily] = useState("materiel");
  const [missing, setMissing] = useState(null); // {member, items}
  const [delItem, setDelItem] = useState(null);
  const [loanOpen, setLoanOpen] = useState(false);
  const [loanForm, setLoanForm] = useState({ item_name: "", item_id: "", user_id: "", loan_date: new Date().toISOString().slice(0, 10) });
  const [delLoan, setDelLoan] = useState(null);
  const [loanSort, setLoanSort] = useState({ key: "loan_date", dir: -1 }); // tri des colonnes Prêts
  const [itemSort, setItemSort] = useState(0);   // 0 défaut, 1 A→Z, -1 Z→A
  const [memberSort, setMemberSort] = useState(0);

  const load = useCallback(() => {
    api.get("/inventory").then((r) => setData(r.data)).catch((e) => toast.error(e.friendly));
    api.get("/loans").then((r) => setLoans(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const stateOf = (itemId, userId) => data.states[itemId]?.[userId] || { owned: false, origin: null };

  const sortedMembers = useMemo(() => {
    if (!memberSort) return data.members;
    return [...data.members].sort((a, b) => memberSort * `${a.prenom} ${a.nom}`.trim().localeCompare(`${b.prenom} ${b.nom}`.trim(), "fr"));
  }, [data.members, memberSort]);
  // Chaque membre ne voit que sa colonne ; l'admin a une vue personnelle ET la matrice complète (éditable)
  const [fullView, setFullView] = useState(true);
  const visibleMembers = useMemo(
    () => (isAdmin && fullView ? sortedMembers : sortedMembers.filter((m) => m.id === user.id)),
    [sortedMembers, isAdmin, fullView, user.id]
  );
  const sortItems = useCallback((list) => (!itemSort ? list : [...list].sort((a, b) => itemSort * a.name.localeCompare(b.name, "fr"))), [itemSort]);
  const obligatoire = useMemo(() => sortItems(data.items.filter((i) => i.category === "obligatoire")), [data.items, sortItems]);
  const facultatif = useMemo(() => sortItems(data.items.filter((i) => i.category === "facultatif")), [data.items, sortItems]);

  const missingFor = (memberId) => obligatoire.filter((it) => !stateOf(it.id, memberId).owned);

  const toggleLoanSort = (key) => setLoanSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));
  const sortedLoans = useMemo(() => {
    const arr = [...loans];
    arr.sort((a, b) => {
      let cmp = 0;
      if (loanSort.key === "loan_date") cmp = a.loan_date.localeCompare(b.loan_date);
      else if (loanSort.key === "item_name") cmp = a.item_name.localeCompare(b.item_name, "fr");
      else if (loanSort.key === "member_name") cmp = (a.member_name || "").localeCompare(b.member_name || "", "fr");
      else cmp = Number(a.returned) - Number(b.returned);
      return loanSort.dir * cmp;
    });
    return arr;
  }, [loans, loanSort]);
  const sortArrow = (active, dir) =>
    !active ? <ArrowUpDown className="h-3 w-3 opacity-40" /> : dir === 1 ? <ArrowUp className="h-3 w-3 accent" /> : <ArrowDown className="h-3 w-3 accent" />;

  // Cycle au clic : non possédé → personnel (vert) → prêté par le groupe (bleu) → non possédé
  const cycleState = async (itemId, targetUserId) => {
    const st = stateOf(itemId, targetUserId);
    const next = !st.owned
      ? { owned: true, origin: "personnel" }
      : st.origin === "prete"
        ? { owned: false, origin: null }
        : { owned: true, origin: "prete" };
    try {
      await api.put("/inventory/state", { item_id: itemId, user_id: targetUserId, ...next });
      setData((d) => ({
        ...d,
        states: { ...d.states, [itemId]: { ...(d.states[itemId] || {}), [targetUserId]: { owned: next.owned, origin: next.owned ? next.origin : null } } },
      }));
      // « Prêté par le groupe » crée/clôture un prêt côté serveur → rafraîchir l'onglet Prêts
      api.get("/loans").then((r) => setLoans(r.data)).catch(() => {});
    } catch (e) { toast.error(e.friendly); }
  };

  const addItem = async () => {
    if (!newItem.trim()) return;    try {
      await api.post("/inventory/items", { name: newItem.trim(), category: newCat, family: newFamily });
      setNewItem("");
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const addLoan = async () => {
    try {
      await api.post("/loans", { ...loanForm, item_id: loanForm.item_id || null });
      toast.success(t("inv.loan_saved"));
      setLoanOpen(false);
      setLoanForm({ item_name: "", item_id: "", user_id: "", loan_date: new Date().toISOString().slice(0, 10) });
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const uploadJust = async (loanId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/loans/${loanId}/justificatif`, fd);
      toast.success(t("inv.just_added"));
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

  const printHtml = (title, head, bodyRows, landscape, footnote) => {
    const w = window.open("", "_blank");
    if (!w) { toast.error(t("inv.popup_blocked")); return; }
    w.document.write(`<!doctype html><html lang="${lang}"><head><meta charset="utf-8"><title>${esc(title)}</title>
      <style>
        @page { size: ${landscape ? "landscape" : "portrait"}; margin: 12mm; }
        body { font-family: system-ui, sans-serif; color: #111; }
        h1 { font-size: 18px; margin: 0; } h2 { font-size: 14px; font-weight: 600; margin: 4px 0 2px; color: #444; }
        p.date { font-size: 11px; color: #666; margin: 0 0 10px; }
        table { border-collapse: collapse; width: 100%; font-size: 11px; }
        th, td { border: 1px solid #999; padding: 4px 6px; text-align: left; }
        th { background: #eee; }
        td.c, th.c { text-align: center; }
        p.note { font-size: 10px; color: #555; margin-top: 8px; }
      </style></head><body>
      <h1>${esc(group?.name || "RANNEA")}</h1><h2>${esc(title)}</h2>
      <p class="date">${esc(t("inv.print_date"))} ${new Date().toLocaleDateString(dateLocale(lang))}</p>
      <table><thead><tr>${head.map((h) => `<th class="${h.c ? "c" : ""}">${esc(h.label)}</th>`).join("")}</tr></thead>
      <tbody>${bodyRows}</tbody></table>
      ${footnote ? `<p class="note">${esc(footnote)}</p>` : ""}
      <script>window.onload = () => window.print();</script></body></html>`);
    w.document.close();
  };

  const printInventory = () => {
    const head = [{ label: t("inv.item_col") }, ...visibleMembers.map((m) => ({ label: `${m.prenom} ${m.nom}`.trim(), c: true }))];
    const cell = (it, m) => { const st = stateOf(it.id, m.id); return st.owned ? (st.origin === "prete" ? "G" : "✓") : "—"; };
    const secRow = (label) => `<tr><td colspan="${visibleMembers.length + 1}" style="background:#f3f3f3;font-weight:700;text-transform:uppercase;font-size:10px">${label}</td></tr>`;
    const itemRow = (it) => `<tr><td>${esc(it.name)}</td>${visibleMembers.map((m) => `<td class="c">${cell(it, m)}</td>`).join("")}</tr>`;
    const rows = [secRow(t("inv.cat_obligatoire")), ...obligatoire.map(itemRow), secRow(t("inv.cat_facultatif")), ...facultatif.map(itemRow)].join("");
    printHtml(t("nav.inventory"), head, rows, true, t("inv.print_footnote"));
  };

  const printLoans = () => {
    const head = [{ label: t("inv.material") }, { label: t("pres.member") }, { label: t("inv.loan_date") }, { label: t("inv.status") }, { label: t("inv.justificatif") }];
    const rows = sortedLoans.map((l) =>
      `<tr><td>${esc(l.item_name)}</td><td>${esc(l.member_name)}</td><td>${new Date(l.loan_date + "T00:00").toLocaleDateString(dateLocale(lang))}</td><td>${l.returned ? t("inv.returned") : t("inv.ongoing")}</td><td>${l.has_justificatif ? t("common.yes") : "—"}</td></tr>`
    ).join("");
    printHtml(t("inv.loans_title"), head, rows, false);
  };

  const ItemRow = ({ item }) => (
    <tr className="border-b border-app/30" data-testid={`inventory-item-row-${item.id}`}>
      <td className="p-3 sticky left-0 card-bg">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{item.name}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${item.family === "tenue" ? "bg-sky-500/15 text-sky-400" : item.family === "instrument" ? "bg-rose-500/15 text-rose-400" : "bg-slate-500/15 text-slate-400"}`} data-testid={`item-family-${item.id}`}>
            {t({ tenue: "inv.family_tenue", instrument: "inv.family_instrument", materiel: "inv.family_materiel" }[item.family] || "inv.family_materiel")}
          </span>
          {isAdmin && (
            <button onClick={() => setDelItem(item)} className="t-secondary hover:text-rose-400" data-testid={`delete-item-${item.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </td>
      {visibleMembers.map((m) => {
        const st = stateOf(item.id, m.id);
        // Éditable : sa propre case, ou toutes les cases pour l'admin en vue complète
        const editable = m.id === user.id || (isAdmin && fullView);
        // Couleur : vert = personnel, bleu = prêté par le groupe, gris = non possédé
        const boxCls = !st.owned
          ? "border-slate-600 bg-transparent"
          : st.origin === "prete"
            ? "border-sky-500 bg-sky-500 text-white"
            : "border-emerald-500 bg-emerald-500 text-white";
        return (
          <td key={m.id} className="p-2 text-center" data-testid={`inventory-cell-${item.id}-${m.id}`}>
            {editable ? (
              <button
                type="button"
                onClick={() => cycleState(item.id, m.id)}
                className={`inline-flex items-center justify-center h-5 w-5 rounded border-2 transition-colors ${boxCls}`}
                title={st.owned ? (st.origin === "prete" ? t("inv.origin_loaned") : t("inv.origin_personnel")) : t("inv.not_owned")}
                data-testid={`inventory-toggle-${item.id}-${m.id}`}
              >
                {st.owned && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
              </button>
            ) : (
              <span
                className={`inline-block h-3.5 w-3.5 rounded-full ${!st.owned ? "bg-slate-600" : st.origin === "prete" ? "bg-sky-400" : "bg-emerald-400"}`}
                title={st.owned ? (st.origin === "prete" ? t("inv.origin_loaned") : t("inv.owned")) : t("inv.not_owned")}
              />
            )}
          </td>
        );
      })}
    </tr>
  );

  return (
    <div className="space-y-4" data-testid="inventory-page">
      <h2 className="font-heading text-2xl font-bold flex items-center gap-2"><Package className="h-6 w-6 brand" /> {t("nav.inventory")}</h2>

      <Tabs defaultValue="inventaire">
        <TabsList className="card-bg border border-app">
          <TabsTrigger value="inventaire" data-testid="tab-inventaire">{t("nav.inventory")}</TabsTrigger>
          <TabsTrigger value="prets" data-testid="tab-prets">{t("inv.tab_loans")}</TabsTrigger>
        </TabsList>

        <TabsContent value="inventaire" className="space-y-4 mt-4">
          {isAdmin && (
            <div className="flex gap-2 flex-wrap app-card p-3">
              <Input value={newItem} onChange={(e) => setNewItem(e.target.value)} placeholder={t("inv.new_item_placeholder")} className="h-9 bg-transparent border-app text-sm w-64" data-testid="new-item-input" />
              <Select value={newCat} onValueChange={setNewCat}>
                <SelectTrigger className="h-9 w-40 bg-transparent border-app" data-testid="new-item-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="obligatoire">{t("inv.cat_obligatoire")}</SelectItem>
                  <SelectItem value="facultatif">{t("inv.cat_facultatif")}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={newFamily} onValueChange={setNewFamily}>
                <SelectTrigger className="h-9 w-40 bg-transparent border-app" data-testid="new-item-family-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tenue">{t("inv.family_tenue")}</SelectItem>
                  <SelectItem value="instrument">{t("inv.family_instrument")}</SelectItem>
                  <SelectItem value="materiel">{t("inv.family_materiel")}</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" className="brand-bg text-white h-9" onClick={addItem} disabled={!newItem.trim()} data-testid="add-item-button">
                <Plus className="h-4 w-4 mr-1" /> {t("common.add")}
              </Button>
            </div>
          )}

          <div className="flex items-center justify-between flex-wrap gap-2">
            {isAdmin ? (
              <div className="flex gap-1 p-1 rounded-lg bg-white/5" data-testid="inventory-view-toggle">
                <button
                  onClick={() => setFullView(false)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${!fullView ? "brand-bg text-white" : "t-secondary hover:text-white"}`}
                  data-testid="inventory-view-mine"
                >
                  {t("inv.view_mine")}
                </button>
                <button
                  onClick={() => setFullView(true)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${fullView ? "brand-bg text-white" : "t-secondary hover:text-white"}`}
                  data-testid="inventory-view-all"
                >
                  {t("inv.view_all")}
                </button>
              </div>
            ) : <span />}
            {/* Légende des couleurs : vert = personnel, bleu = prêté par le groupe, gris = non possédé */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs t-secondary" data-testid="inventory-legend">
              <span className="flex items-center gap-1.5"><span className="inline-block h-3.5 w-3.5 rounded-full bg-emerald-400" /> {t("inv.origin_personnel")}</span>
              <span className="flex items-center gap-1.5"><span className="inline-block h-3.5 w-3.5 rounded-full bg-sky-400" /> {t("inv.origin_loaned")}</span>
              <span className="flex items-center gap-1.5"><span className="inline-block h-3.5 w-3.5 rounded-full bg-slate-600" /> {t("inv.not_owned")}</span>
            </div>
            <Button variant="outline" size="sm" className="border-app h-9" onClick={printInventory} data-testid="print-inventory-button">
              <Printer className="h-4 w-4 mr-1" /> {t("inv.print")}
            </Button>
          </div>

          <div className="flex items-center justify-end gap-1.5" data-testid="inventory-zoom">
            <button
              onClick={() => zoomBy(-0.05)}
              disabled={zoom <= 0.6}
              className="h-8 w-8 rounded-lg border border-app t-secondary hover:bg-white/5 hover:text-white transition-colors inline-flex items-center justify-center disabled:opacity-30"
              title={t("pres.zoom_out")} aria-label={t("pres.zoom_out")}
              data-testid="inventory-zoom-out"
            >
              <Minus className="h-4 w-4" />
            </button>
            <span className="text-xs t-secondary w-11 text-center tabular-nums" data-testid="inventory-zoom-level">{Math.round(zoom * 100)} %</span>
            <button
              onClick={() => zoomBy(0.05)}
              disabled={zoom >= 1}
              className="h-8 w-8 rounded-lg border border-app t-secondary hover:bg-white/5 hover:text-white transition-colors inline-flex items-center justify-center disabled:opacity-30"
              title={t("pres.zoom_in")} aria-label={t("pres.zoom_in")}
              data-testid="inventory-zoom-in"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
          <div className="app-card overflow-x-auto" style={{ zoom }} data-testid="inventory-matrix">
            <table className="w-full text-sm" data-testid="inventory-table">
              <thead>
                <tr className="border-b border-app">
                  <th className="text-left p-3 font-heading sticky left-0 card-bg min-w-44 cursor-pointer select-none" onClick={() => setItemSort((s) => (s === 1 ? -1 : 1))} data-testid="inventory-sort-item">
                    <span className="inline-flex items-center gap-1">{t("inv.item_col")} {sortArrow(itemSort !== 0, itemSort)}</span>
                  </th>
                  {visibleMembers.map((m) => (
                    <th key={m.id} className="p-2 text-center min-w-24 cursor-pointer select-none hover:bg-white/5" onClick={() => setMemberSort((s) => (s === 1 ? -1 : 1))} data-testid={`inventory-sort-member-${m.id}`}>
                      <div className="text-xs font-semibold inline-flex items-center gap-1">{m.prenom} {sortArrow(memberSort !== 0, memberSort)}</div>
                      <div className="text-[10px] t-secondary font-normal">{m.instrument}</div>
                    </th>
                  ))}
                </tr>
                <tr className="border-b border-app bg-white/[0.03]">
                  <td className="p-3 text-xs font-bold uppercase tracking-wider sticky left-0 card-bg">{t("inv.complete")}</td>
                  {visibleMembers.map((m) => {
                    const miss = missingFor(m.id);
                    return (
                      <td key={m.id} className="p-2 text-center">
                        <button
                          onClick={() => miss.length > 0 && setMissing({ member: m, items: miss })}
                          className={`inline-flex items-center justify-center h-6 w-6 rounded-full ${miss.length === 0 ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/20 text-amber-400"}`}
                          data-testid={`complet-indicator-${m.id}`}
                        >
                          {miss.length === 0 ? <Check className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                <tr><td colSpan={visibleMembers.length + 1} className="px-3 py-2 text-xs font-bold uppercase tracking-wider accent sticky left-0 card-bg border-b border-app/50">{t("inv.cat_obligatoire")}</td></tr>
                {obligatoire.map((it) => <ItemRow key={it.id} item={it} />)}
                <tr><td colSpan={visibleMembers.length + 1} className="px-3 py-2 text-xs font-bold uppercase tracking-wider t-secondary sticky left-0 card-bg border-b border-app/50">{t("inv.cat_facultatif")}</td></tr>
                {facultatif.map((it) => <ItemRow key={it.id} item={it} />)}
              </tbody>
            </table>
            {data.items.length === 0 && (
              <p className="text-sm t-secondary p-6 text-center">{t("inv.no_items")}{isAdmin ? t("inv.no_items_admin") : ""}</p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="prets" className="space-y-4 mt-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-heading text-lg font-bold flex items-center gap-2"><HandCoins className="h-5 w-5 accent" /> {t("inv.loans_title")}</h3>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="border-app" onClick={printLoans} data-testid="print-loans-button">
                <Printer className="h-4 w-4 mr-1" /> {t("inv.print")}
              </Button>
              {isAdmin && (
                <Button className="brand-bg text-white" size="sm" onClick={() => setLoanOpen(true)} data-testid="add-loan-button">
                  <Plus className="h-4 w-4 mr-1" /> {t("inv.new_loan")}
                </Button>
              )}
            </div>
          </div>
          <div className="app-card overflow-x-auto">
            <table className="w-full text-sm" data-testid="loans-table">
              <thead>
                <tr className="border-b border-app text-left">
                  <th className="p-3 cursor-pointer select-none hover:bg-white/5" onClick={() => toggleLoanSort("item_name")} data-testid="loans-sort-item_name">
                    <span className="inline-flex items-center gap-1">{t("inv.material")} {sortArrow(loanSort.key === "item_name", loanSort.dir)}</span>
                  </th>
                  <th className="p-3 cursor-pointer select-none hover:bg-white/5" onClick={() => toggleLoanSort("member_name")} data-testid="loans-sort-member_name">
                    <span className="inline-flex items-center gap-1">{t("pres.member")} {sortArrow(loanSort.key === "member_name", loanSort.dir)}</span>
                  </th>
                  <th className="p-3 cursor-pointer select-none hover:bg-white/5" onClick={() => toggleLoanSort("loan_date")} data-testid="loans-sort-loan_date">
                    <span className="inline-flex items-center gap-1">{t("inv.loan_date")} {sortArrow(loanSort.key === "loan_date", loanSort.dir)}</span>
                  </th>
                  <th className="p-3">{t("inv.justificatif")}</th>
                  <th className="p-3 cursor-pointer select-none hover:bg-white/5" onClick={() => toggleLoanSort("returned")} data-testid="loans-sort-returned">
                    <span className="inline-flex items-center gap-1">{t("inv.status")} {sortArrow(loanSort.key === "returned", loanSort.dir)}</span>
                  </th>
                  {isAdmin && <th className="p-3"></th>}
                </tr>
              </thead>
              <tbody>
                {sortedLoans.map((l) => (
                  <tr key={l.id} className="border-b border-app/30" data-testid={`loan-row-${l.id}`}>
                    <td className="p-3 font-medium">{l.item_name}</td>
                    <td className="p-3">{l.member_name}</td>
                    <td className="p-3">
                      {isAdmin ? (
                        <input
                          type="date"
                          value={l.loan_date}
                          onChange={async (e) => {
                            const v = e.target.value;
                            if (!v || v === l.loan_date) return;
                            try {
                              await api.put(`/loans/${l.id}`, { loan_date: v });
                              toast.success(t("inv.date_updated"));
                              load();
                            } catch (err) { toast.error(err.friendly); }
                          }}
                          className="bg-transparent border border-app rounded px-2 py-1 text-xs"
                          data-testid={`loan-date-${l.id}`}
                        />
                      ) : (
                        new Date(l.loan_date + "T00:00").toLocaleDateString(dateLocale(lang))
                      )}
                    </td>
                    <td className="p-3">
                      {l.has_justificatif ? (
                        <a href={loanJustificatifUrl(l.id)} target="_blank" rel="noreferrer" className="brand text-xs flex items-center gap-1 hover:underline" data-testid={`loan-justificatif-${l.id}`}>
                          <FileCheck className="h-3.5 w-3.5" /> {t("inv.view")}
                        </a>
                      ) : isAdmin ? (
                        <label className="cursor-pointer text-xs t-secondary hover:text-white flex items-center gap-1">
                          <input type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => e.target.files?.[0] && uploadJust(l.id, e.target.files[0])} data-testid={`loan-upload-just-${l.id}`} />
                          <Upload className="h-3.5 w-3.5" /> {t("inv.attach")}
                        </label>
                      ) : <span className="text-xs t-secondary">—</span>}
                    </td>
                    <td className="p-3">
                      {isAdmin ? (
                        <button
                          onClick={async () => { await api.put(`/loans/${l.id}`, { returned: !l.returned }).catch(() => {}); load(); }}
                          className={`text-xs px-2.5 py-1 rounded-full border ${l.returned ? "bg-slate-500/15 text-slate-400 border-slate-500/30" : "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"}`}
                          data-testid={`loan-returned-toggle-${l.id}`}
                        >
                          {l.returned ? t("inv.returned") : t("inv.ongoing")}
                        </button>
                      ) : (
                        <span className={`text-xs px-2.5 py-1 rounded-full border ${l.returned ? "bg-slate-500/15 text-slate-400 border-slate-500/30" : "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"}`}>
                          {l.returned ? t("inv.returned") : t("inv.ongoing")}
                        </span>
                      )}
                    </td>
                    {isAdmin && (
                      <td className="p-3">
                        <button onClick={() => setDelLoan(l)} className="t-secondary hover:text-rose-400" data-testid={`loan-delete-${l.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {loans.length === 0 && <p className="text-sm t-secondary p-6 text-center">{t("inv.no_loans")}</p>}
          </div>
        </TabsContent>
      </Tabs>

      {/* Éléments manquants */}
      <Dialog open={!!missing} onOpenChange={() => setMissing(null)}>
        <DialogContent className="card-bg border-app" data-testid="missing-items-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("inv.missing_title")} — {missing?.member.prenom} {missing?.member.nom}</DialogTitle>
          </DialogHeader>
          <ul className="space-y-2">
            {missing?.items.map((it) => (
              <li key={it.id} className="flex items-center gap-2 text-sm">
                <AlertTriangle className="h-4 w-4 text-amber-400" /> {it.name}
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!delItem}
        title={t("inv.delete_item_title")}
        description={t("inv.delete_item_desc", { name: delItem?.name })}
        onConfirm={async () => {
          await api.delete(`/inventory/items/${delItem.id}`).catch((e) => toast.error(e.friendly));
          setDelItem(null);
          load();
        }}
        onClose={() => setDelItem(null)}
        testid="delete-item-confirm-dialog"
      />

      <ConfirmDialog
        open={!!delLoan}
        title={t("inv.delete_loan_title")}
        description={t("inv.delete_loan_desc", { name: delLoan?.item_name })}
        onConfirm={async () => {
          await api.delete(`/loans/${delLoan.id}`).catch((e) => toast.error(e.friendly));
          setDelLoan(null);
          load();
        }}
        onClose={() => setDelLoan(null)}
        testid="delete-loan-confirm-dialog"
      />

      {/* Nouveau prêt */}
      <Dialog open={loanOpen} onOpenChange={setLoanOpen}>
        <DialogContent className="card-bg border-app" data-testid="add-loan-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("inv.new_loan")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("inv.material")}</Label>
              <Select value={loanForm.item_id || "autre"} onValueChange={(v) => setLoanForm({ ...loanForm, item_id: v === "autre" ? "" : v })}>
                <SelectTrigger className="mt-1 bg-transparent border-app" data-testid="loan-item-select"><SelectValue placeholder={t("usr.choose")} /></SelectTrigger>
                <SelectContent>
                  {data.items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                  <SelectItem value="autre">{t("inv.other_free")}</SelectItem>
                </SelectContent>
              </Select>
              {!loanForm.item_id && (
                <Input value={loanForm.item_name} onChange={(e) => setLoanForm({ ...loanForm, item_name: e.target.value })} placeholder={t("inv.specify_material")} className="mt-2 bg-transparent border-app" data-testid="loan-item-input" />
              )}
            </div>
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("inv.recipient")}</Label>
              <Select value={loanForm.user_id} onValueChange={(v) => setLoanForm({ ...loanForm, user_id: v })}>
                <SelectTrigger className="mt-1 bg-transparent border-app" data-testid="loan-member-select"><SelectValue placeholder={t("usr.choose")} /></SelectTrigger>
                <SelectContent>
                  {data.members.map((m) => <SelectItem key={m.id} value={m.id}>{m.prenom} {m.nom}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("inv.loan_date")}</Label>
              <Input type="date" value={loanForm.loan_date} onChange={(e) => setLoanForm({ ...loanForm, loan_date: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="loan-date-input" />
            </div>
            <Button className="w-full brand-bg text-white" onClick={addLoan} disabled={(!loanForm.item_id && !loanForm.item_name.trim()) || !loanForm.user_id} data-testid="loan-save-button">
              {t("inv.save_loan")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
